'use client';

import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  BodyDefault,
  LinkedFindingRow,
  MetaDefault,
  getStatusIcon,
  isArchivedLifecycle,
} from '@terra/ui-design-system';
import { ArrowRight01Icon } from '@terra/ui-icons';
import type { GroupedSignal } from '@terra/shared-contracts-signals';
import type { OpenMainContentTabInput } from '../../../store/layoutStore';
import { useDateFormatter } from '../../../hooks/useDateFormatter';
import {
  relatedFindingFromSignalLinkedPreview,
  toLinkedFindingRowData,
} from '../../../hooks/api/signal-related-finding';
import { SignalAssetLinkRow } from '../SignalAssetLinkRow';
import {
  buildNeoExecutionTab,
  buildSignalExecutionTab,
  buildSignalFindingTab,
} from './signal-tabs.model';
import {
  sortSignalNeoTracesNewestFirst,
  type SignalTrace,
} from './signals-list.model';
import { SIGNAL_NEO_STATUS } from './signal-neo-status.model';
import { SignalNeoStatusIcon } from './SignalActionsCell';
import {
  useSignalRowActions,
  type SignalRowActions,
} from './useSignalRowActions';
import { useSignalsTableContext } from './SignalsTableContext';

function RelatedLinkSurface({
  tab,
  children,
}: {
  tab: OpenMainContentTabInput | undefined;
  children: ReactNode;
}) {
  const { navigation } = useSignalsTableContext();

  return (
    <Box
      px={3}
      py={2.5}
      bg="bg.default"
      borderWidth="1px"
      borderColor="border.neutral.subtle"
      borderRadius="l2"
      cursor={tab ? 'pointer' : 'default'}
      _hover={tab ? { bg: 'bg.neutral.subtleSoft' } : undefined}
      onClick={(event) => {
        event.stopPropagation();
        navigation.openTab(tab);
      }}
      onContextMenu={(event) => navigation.openTabContextMenu({ event, tab })}
    >
      {children}
    </Box>
  );
}

function RelatedLinkArrow() {
  return (
    <Box flexShrink={0} display="inline-flex" color="fg.neutral.subtle">
      <ArrowRight01Icon size={14} />
    </Box>
  );
}

function SignalExecutionLinks({
  signal,
  traces,
  liveRun,
}: {
  signal: GroupedSignal;
  traces: readonly SignalTrace[];
  liveRun: Pick<
    SignalRowActions,
    'neoStatus' | 'neoThreadId' | 'neoTooltip'
  > | null;
}) {
  const tStatus = useTranslations('designSystem.executions.status');
  const dateFormatter = useDateFormatter();

  if (traces.length === 0 && !liveRun) {
    return null;
  }

  return (
    <>
      {liveRun ? (
        <RelatedLinkSurface
          tab={
            liveRun.neoThreadId
              ? buildNeoExecutionTab(liveRun.neoThreadId)
              : undefined
          }
        >
          <HStack gap={2}>
            <SignalNeoStatusIcon status={liveRun.neoStatus} />
            <BodyDefault truncate flex={1}>
              {liveRun.neoTooltip}
            </BodyDefault>
            {liveRun.neoThreadId ? <RelatedLinkArrow /> : null}
          </HStack>
        </RelatedLinkSurface>
      ) : null}
      {traces.map((trace) => (
        <RelatedLinkSurface
          key={trace.id}
          tab={buildSignalExecutionTab({ signal, trace })}
        >
          <HStack gap={2}>
            <Box flexShrink={0} display="inline-flex">
              {getStatusIcon(trace.status, 12)}
            </Box>
            <BodyDefault truncate flex={1}>
              {`${trace.name.toUpperCase()} · ${tStatus(trace.status)}`}
            </BodyDefault>
            <MetaDefault color="fg.neutral.subtle" whiteSpace="nowrap">
              {dateFormatter.formatDistance(
                new Date(trace.createdAt),
                new Date(),
                {
                  addSuffix: true,
                },
              )}
            </MetaDefault>
            <RelatedLinkArrow />
          </HStack>
        </RelatedLinkSurface>
      ))}
    </>
  );
}

function SignalSurfaceAssetLink({ signal }: { signal: GroupedSignal }) {
  const tSignals = useTranslations('signals');
  const { navigation } = useSignalsTableContext();
  const tab = navigation.getRelatedAssetTab(signal);

  if (signal.endpoint || !tab) {
    return null;
  }

  return (
    <SignalAssetLinkRow
      label={tab.title}
      canNavigate
      tooltipTitle={tSignals('goToAssetTooltip', { asset: tab.title })}
      onNavigate={() => navigation.openTab(tab)}
      onContextMenu={(event) => navigation.openTabContextMenu({ event, tab })}
      stopPropagationOnClick
      isArchived={isArchivedLifecycle(
        signal.relatedAssets?.find((asset) => asset.id === signal.assetId)
          ?.lifecycle,
      )}
    />
  );
}

export function SignalRelatedLinks({ signal }: { signal: GroupedSignal }) {
  const t = useTranslations('proxy.signals.detail');
  const rowActions = useSignalRowActions(signal);
  const finding = signal.linkedFinding;
  const traces = sortSignalNeoTracesNewestFirst(signal.traces);
  const liveRun =
    rowActions.neoStatus !== SIGNAL_NEO_STATUS.IDLE &&
    !traces.some((trace) => trace.threadId === rowActions.neoThreadId)
      ? rowActions
      : null;
  const hasRelated =
    Boolean(finding) ||
    traces.length > 0 ||
    liveRun !== null ||
    Boolean(!signal.endpoint && signal.assetId);

  return (
    <VStack gap={2} alignItems="stretch">
      <SignalExecutionLinks signal={signal} traces={traces} liveRun={liveRun} />
      {finding ? (
        <RelatedLinkSurface tab={buildSignalFindingTab(signal)}>
          <LinkedFindingRow
            embeddedInSurface
            finding={toLinkedFindingRowData(
              relatedFindingFromSignalLinkedPreview({
                signalId: signal.id,
                linkedFinding: finding,
              }),
            )}
          />
        </RelatedLinkSurface>
      ) : null}
      <SignalSurfaceAssetLink signal={signal} />
      {hasRelated ? null : (
        <MetaDefault color="fg.neutral.subtle">{t('noRelated')}</MetaDefault>
      )}
    </VStack>
  );
}
